import {
  createAddFlowDiagnostic,
  type AddFlowOutcome
} from '#~/agent/components/bridge/add-flow-diagnostic.js';
import { toUrx, type Minimist } from '#~/device/index.js';
import { logger } from '#~/logger/index.js';
import { useProducer, useStore } from './hooks.js';
import { Context } from './context.js';
import { ensureNotNil } from '@studio/js-utils';
import type { Draft, Preview, Producer } from './types.js';
import { describeAddDiscovery, describeAddFailure, traceCameraAdd } from '#~/device/add-trace.js';
import { addDevice, removePreview, urxCache } from './utils.js';
import { classifyPreviewPrepareCause } from './preview-outcome.js';
import { runPreviewPrepare } from './preview-prepare.js';
import { useAuth0 } from '#~/auth/index.js';
import type { ChildrenOnly } from '@studio/design-system/props';
import type { Agent } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import { getTransientId, type Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { useDeepEffect } from '@studio/react-utils/hooks';
import { toObjectEntries, type T_Optional } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import { useContext, useRef, type ReactElement } from 'react';
import { shallow } from 'zustand/shallow';

// Serializing raw setup across mounts prevents reopening Add from duplicating a pending mutation.
let currentSetup: Promise<void> | undefined;
// A stale add can allocate before cancellation is observed. Retain its cleanup until confirmed.
const abandonedPreviews = new Map<Device['id'], Preview>();

/** Bound removal waits so an unresponsive Bridge cannot wedge Save or the next dialog. */
export const PREVIEW_CLEANUP_DEADLINE_MS = 10_000;

/** Lets a settlePreviewSetup caller tell a hung cleanup (proceed) from a real removal failure (retry). */
export interface PreviewSetupOutcome {
  /** The cleanup exceeded its budget and is still running best-effort; the caller should proceed. */
  timedOut: boolean;
  /** The cleanup rejected (a real removal failure); the caller keeps the attempt retryable. */
  error?: unknown;
}

/** Retains a never-settled preview so a later setup flight can re-attempt its removal. */
export const abandonPreview = (preview: Preview): void => {
  abandonedPreviews.set(preview.deviceId, preview);
};

/** Waits on the prior chain for at most the budget so a hung flight cannot serialize this owner. */
const withinBudget = (previous: Promise<unknown> | undefined, budgetMs: number): Promise<void> =>
  previous === undefined
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
        let done = false;
        let timer: ReturnType<typeof setTimeout>;
        const finish = (): void => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve();
        };
        timer = setTimeout(finish, budgetMs);
        void Promise.resolve(previous).then(finish, finish);
      });

/**
 * Save/close waits for raw setup before removing its final records or adding a permanent device.
 * With a deadline it never blocks longer than the budget: on timeout it drops this flight from the
 * shared chain (identity-guarded) and reports `timedOut`, so the caller can proceed and the next
 * owner is not chained behind a hung removal. The flight itself keeps running best-effort.
 */
export const settlePreviewSetup = (
  getToken: () => Promise<string>,
  cleanup: (token: string) => Promise<void>,
  deadlineMs?: number
): Promise<PreviewSetupOutcome> => {
  const previous = currentSetup;
  const flight = (async () => {
    await previous;
    const token = await getToken();
    // A stale mutation can allocate even when its first removal failed. Confirm cleanup first.
    for (const preview of abandonedPreviews.values()) {
      await removePreview(preview, token);
      abandonedPreviews.delete(preview.deviceId);
    }
    await cleanup(token);
  })();
  // Reopening waits for close cleanup too. The caller retains any failure; later owners may retry.
  const settled = flight
    .catch(() => {})
    .finally(() => {
      if (currentSetup === settled) currentSetup = undefined;
    });
  currentSetup = settled;
  // Surface the flight's outcome without rejecting so the caller can branch on hung-vs-failed.
  const observed: Promise<PreviewSetupOutcome> = flight.then(
    () => ({ timedOut: false }),
    (error) => ({ timedOut: false, error })
  );
  if (deadlineMs === undefined) return observed;
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<PreviewSetupOutcome>((resolve) => {
    timer = setTimeout(() => {
      // Per B2: a past-budget flight must not remain the chain for the next owner — drop it.
      if (currentSetup === settled) currentSetup = undefined;
      resolve({ timedOut: true });
    }, deadlineMs);
  });
  return Promise.race([observed, deadline]).finally(() => clearTimeout(timer));
};

const previewSourceKey = (candidate: Omit<Minimist, 'id' | 'agentId'>): string =>
  JSON.stringify(candidate);

type Aspect = 'audio' | 'control' | 'video';

const ASPECTS: readonly Aspect[] = ['audio', 'control', 'video'];

/** The URX of the preview device for one aspect of the candidate. */
const toPreviewUrx = (candidate: Omit<Minimist, 'id' | 'agentId'>, key: Aspect) =>
  toUrx({ [key]: candidate[key] }, { isTransient: true, modelId: candidate.modelId });

/**
 * The add for `deviceId` settled, accepted or rejected, so it is no longer in flight.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global), both outcomes call this.
 */
const settlePendingPreview = (draft: Draft, key: Aspect, deviceId: Device['id']): void => {
  if (draft.pendingPreviews[key]?.deviceId === deviceId) {
    draft.pendingPreviews[key] = undefined;
  }
};

export const syncCandidate = async ({
  candidate,
  token,
  producer,
  appId,
  roomId,
  agentId,
  previews,
  remove,
  isCurrent,
  forceVideo,
  forceAll,
  report
}: {
  candidate: Omit<Minimist, 'id' | 'agentId'>;
  token: string;
  producer: Producer;
  appId: AppId;
  roomId: Room['id'];
  agentId: Agent['id'];
  previews: T_Optional<Record<'audio' | 'video' | 'control', Preview>, undefined>;
  remove: boolean;
  isCurrent?: () => boolean;
  /** Retry tears down and recreates only the owned video preview. */
  forceVideo?: boolean;
  forceAll?: boolean;
  report?: ((outcome: AddFlowOutcome) => void) | undefined;
}) => {
  const live = (): boolean => isCurrent?.() ?? true;
  const { audio, control, video, ...rest } = candidate;
  const config = { audio, control, video };

  const removeCapturedPreview = async (key: keyof typeof previews, preview: Preview) => {
    try {
      await removePreview(preview, token);
    } catch (cause) {
      const outcome = classifyPreviewPrepareCause(cause, 'preview-prepare', 'cleanup');
      report?.({
        operation: 'preview-remove',
        category: 'unknown',
        retry: 'check-state',
        status: outcome.status,
        deviceId: preview.deviceId
      });
      throw cause;
    }
    // Confirmed removal outlives UI currency; never erase a different replacement.
    producer((draft) => {
      if (draft.previews[key]?.deviceId === preview.deviceId) draft.previews[key] = undefined;
    });
  };

  for (const preview of abandonedPreviews.values()) {
    if (!live()) return;
    await removePreview(preview, token);
    abandonedPreviews.delete(preview.deviceId);
  }

  for (const [key, aspect] of toObjectEntries(config)) {
    const preview = previews[key];
    const urx = toPreviewUrx(candidate, key);
    const forceThis = forceAll || (forceVideo === true && key === 'video');

    if (!live()) return;

    if (urx) {
      if (preview?.urx === urx && !remove && !forceThis) {
        continue;
      }

      if (preview) {
        // Rejection leaves the captured record available for a later cleanup attempt.
        await removeCapturedPreview(key, preview);
        if (!live()) return;
      }

      if (!live()) {
        return;
      }

      if (remove || (urxCache[urx] && !forceThis)) {
        continue;
      }

      const deviceId = getTransientId(appId);

      logger.debug(`Adding preview ${key} device: ${deviceId}`);
      const traceOnvif = (event: string, details: Record<string, unknown> = {}) => {
        if (key !== 'control' || control?.protocol !== 'onvif') return;
        traceCameraAdd(event, {
          deviceId,
          roomId,
          targetAgentId: agentId,
          discovery: describeAddDiscovery(candidate),
          ...details
        });
      };
      traceOnvif('single.preview-start');

      // The add is in flight until the Bridge answers it (State['pendingPreviews']).
      producer((draft) => {
        draft.pendingPreviews[key] = { deviceId, urx };
      });

      try {
        const result = await addDevice(
          roomId,
          agentId,
          {
            agentId,
            id: deviceId,
            ...rest,
            [key]: aspect
          },
          token,
          urx,
          { isPreviewCurrent: live }
        );
        traceOnvif('single.preview-acknowledged');

        // A superseded receipt proves the transient mutation was never sent.
        if (result?.kind === 'superseded') return;

        if (!live()) {
          abandonedPreviews.set(deviceId, { deviceId, urx });
          await removePreview({ deviceId, urx }, token);
          abandonedPreviews.delete(deviceId);
          return;
        }
        abandonedPreviews.delete(deviceId);

        producer((draft) => {
          draft.previews[key] = { deviceId, urx };
          settlePendingPreview(draft, key, deviceId);
        });
      } catch (e) {
        const error = e as Error;
        traceOnvif('single.preview-rejected', { failure: describeAddFailure(error) });

        const outcome = classifyPreviewPrepareCause(error, 'preview-prepare', 'preview');
        report?.({
          operation: outcome.operation,
          category: outcome.transient ? 'service' : 'unknown',
          status: outcome.status,
          retry: 'retry',
          deviceId
        });

        if (!live()) {
          // A failed/uncertain add may have allocated; the next owner must confirm cleanup.
          abandonedPreviews.set(deviceId, { deviceId, urx });
          try {
            await removePreview({ deviceId, urx }, token);
            abandonedPreviews.delete(deviceId);
          } catch {
            /* The retained record blocks replacement until cleanup succeeds. */
          }
          return;
        }
        abandonedPreviews.delete(deviceId);

        producer((draft) => {
          draft.previews[key] = { deviceId, error, urx };
          settlePendingPreview(draft, key, deviceId);
        });
      } finally {
        // Superseded and stale receipts settle their own pending record as well.
        producer((draft) => settlePendingPreview(draft, key, deviceId));
      }

      continue;
    }

    if (preview) {
      await removeCapturedPreview(key, preview);
      if (!live()) return;
    }
  }
};

/** Synchronizes the Add candidate with its transient previews on the selected Bridge. */
export const Mediator = ({ children }: ChildrenOnly): ReactElement => {
  const producer = useProducer();
  const store = ensureNotNil(useContext(Context));
  const { getAccessTokenSilently } = useAuth0();
  const { agentId, appId, candidate, previewGeneration, previewPaused, roomId } = useStore(
    (µ) =>
      pick(µ, ['agentId', 'roomId', 'appId', 'candidate', 'previewGeneration', 'previewPaused']),
    shallow
  );
  const bulkMode = useStore(({ source }) => source.bulkMode);
  const applied = useRef({ agentId, roomId, generation: previewGeneration });
  const latest = useRef({
    agentId,
    roomId,
    source: previewSourceKey(candidate),
    previewGeneration
  });
  latest.current = { agentId, roomId, source: previewSourceKey(candidate), previewGeneration };
  const owningAttempt = useRef<object | undefined>(undefined);

  useDeepEffect(() => {
    if (previewPaused || bulkMode) return;
    // Candidate changes release only the obsolete pending preview identity.
    producer((draft) => {
      for (const key of ASPECTS) {
        const pending = draft.pendingPreviews[key];
        if (pending && pending.urx !== toPreviewUrx(candidate, key)) {
          draft.pendingPreviews[key] = undefined;
        }
      }
    });
    let active = true;
    const identity = latest.current;
    const owner = {};
    owningAttempt.current = owner;
    const isCurrent = (): boolean =>
      active &&
      !store.getState().previewPaused &&
      !store.getState().source.bulkMode &&
      identity.agentId === latest.current.agentId &&
      identity.roomId === latest.current.roomId &&
      identity.source === latest.current.source &&
      identity.previewGeneration === latest.current.previewGeneration;
    const diagnostic = createAddFlowDiagnostic({
      actor: 'selected-bridge',
      agentId,
      roomId,
      sourceId: store.getState().source.selection?.id?.toString()
    });
    const previous = currentSetup;
    producer((draft) => {
      draft.previewReady = false;
      draft.previewAvailability = 'preparing';
      draft.previewCopy = undefined;
    });

    // Every queued owner gets a presentation deadline, but only the live owner reaches IO.
    const flight = runPreviewPrepare({
      getToken: async () => {
        // Per B2: a hung previous setup must not chain this owner forever; bound the wait,
        // then start on the current identity. knowledge: addDevice-single-flight-pattern.
        await withinBudget(previous, PREVIEW_CLEANUP_DEADLINE_MS);
        return isCurrent() ? getAccessTokenSilently() : '';
      },
      sync: async (token) => {
        await syncCandidate({
          agentId,
          appId,
          candidate,
          // Queued setup reads records after the previous owner's confirmed removals.
          previews: store.getState().previews,
          producer,
          roomId,
          token,
          remove: false,
          isCurrent,
          forceVideo: previewGeneration !== applied.current.generation,
          forceAll: agentId !== applied.current.agentId || roomId !== applied.current.roomId,
          report: diagnostic.report
        });
        if (!isCurrent()) return;
        applied.current = { agentId, roomId, generation: previewGeneration };
        producer((draft) => {
          const error = draft.previews.video?.error;
          draft.previewReady = !error;
          if (error) {
            const outcome = classifyPreviewPrepareCause(
              error,
              'preview-prepare',
              diagnostic.reference
            );
            diagnostic.report({
              operation: outcome.operation,
              category: outcome.transient ? 'service' : 'unknown',
              status: outcome.status,
              retry: 'retry',
              deviceId: draft.previews.video?.deviceId
            });
            draft.previewCopy = outcome.copy;
            draft.previewAvailability = 'unavailable';
          } else {
            draft.previewCopy = undefined;
            draft.previewAvailability = 'preparing';
          }
        });
      },
      isCurrent,
      report: (outcome) =>
        diagnostic.report({
          operation: outcome.operation,
          category: outcome.timedOut ? 'timeout' : outcome.transient ? 'service' : 'unknown',
          status: outcome.status,
          retry: outcome.timedOut ? 'wait' : 'retry'
        }),
      sink: {
        setCopy: (copy) =>
          producer((draft) => {
            draft.previewCopy = copy;
          }),
        setUnavailable: () =>
          producer((draft) => {
            draft.previewAvailability = 'unavailable';
          }),
        setPending: (pending) =>
          producer((draft) => {
            draft.previewPending = pending;
          })
      },
      reference: diagnostic.reference
    }).finally(() => {
      diagnostic.report({
        operation: 'preview-prepare',
        category: isCurrent() ? 'success' : 'superseded',
        retry: 'none',
        deviceId: store.getState().previews.video?.deviceId
      });
      // Per rule: settle-every-terminal-branch — release only the captured store owner.
      if (owningAttempt.current === owner)
        producer((draft) => {
          draft.previewPending = false;
        });
      if (currentSetup === flight) currentSetup = undefined;
    });
    currentSetup = flight;

    return () => {
      active = false;
    };
  }, [candidate, previewGeneration, previewPaused, agentId, roomId, appId, bulkMode]);

  return <>{children}</>;
};
